import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ColumnDef, FilterFn } from '@tanstack/react-table';
import axios from 'axios';

import DataTable from '@/components/ui/DataTable/DataTable';
import { Col } from '@/components/ui/DataTable/ColumnsHelpers';
import Button from '@/components/ui/Button';
import Alert from '@/components/ui/Alert';
import ConfirmationDialog from '@/components/ui/ConfirmationDialog/ConfirmationDialog';
import MainDialogLayout from '@/components/ui/MainDialogLayout/MainDialogLayout';
import useAuthContext from '@/contexts/Auth/useAuthContext';
import { useGetAuthApi } from '@/hooks/UseApiCalls';
import { putAuth } from '@/api/ApiCalls';
import { formatDateLocale } from '@/helpers/dateHelper';
import { COORDINATOR_ADD_REQUESTS_URL, CoordinatorAddRequestDtoOut, asUtc } from '@/models/out/CoordinatorAddRequestDtoOut';
import { describeWorlds, summaryLevel } from '@/helpers/permissionWorlds';
import { PermissionChip } from '@/pages/FundSettings/people/PeopleDialogs';
import CoordinatorAddRequestBlade from './CoordinatorAddRequestBlade';
import peopleStyles from '@/pages/FundSettings/people/People.module.css';
import styles from './CoordinatorAddRequestsPage.module.css';

const PREFIX = 'coordinatorAddRequests';

const nameOf = (row: CoordinatorAddRequestDtoOut) => `${row.firstName ?? ''} ${row.lastName ?? ''}`.trim() || row.email;
const fundOf = (row: CoordinatorAddRequestDtoOut) => (row.fundName ? `${row.fundName} (${row.fundId})` : `${row.fundId}`);

/*
 * The board places rows at a fixed pitch, two lines high here (twoLineRows): every cell keeps to a
 * main line and a sub-line, each cut with an ellipsis and shown in full on hover. The coordinator cell
 * stacked four lines and the permission wrapped, so rows ran over each other (HAT-10552, 3.11.2026);
 * everything is also in the row's blade.
 */
const LINE = 'block truncate';
const NAME_LINE = 'flex items-baseline gap-2 min-w-0';
const NAME = 'body-md-semibold truncate shrink-0 max-w-[60%]';
const BESIDE = 'body-sm-regular truncate min-w-0 text-(--font-body-2)';
const SUB = `body-sm-regular text-(--font-body-2) ${LINE}`;

/** The search box finds a row by the project, the coordinator or who asked - name, email or number. */
const searchRow: FilterFn<CoordinatorAddRequestDtoOut> = (row, _columnId, query: string) => {
  const r = row.original;
  const text = [fundOf(r), nameOf(r), r.email, r.israeliId, r.phone, r.city, r.requestedByName, r.requestedByEmail].join(' ').toLowerCase();
  return text.includes(
    String(query ?? '')
      .trim()
      .toLowerCase()
  );
};

/** The answers come back as plain Hebrew sentences; anything else gets the generic one. */
const serverText = (err: unknown, fallback: string) =>
  axios.isAxiosError(err) && typeof err.response?.data === 'string' && err.response.data.trim() ? err.response.data : fallback;

/** «דחייה»: the office writes the lead coordinator why; the mail carries it. */
const RejectDialog = ({ row, onClose, onDone }: { row: CoordinatorAddRequestDtoOut; onClose: () => void; onDone: () => void }) => {
  const { t } = useTranslation();
  const { currentUser } = useAuthContext();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isPending, setIsPending] = useState(false);

  const submit = async () => {
    if (!reason.trim()) return setError(t(`${PREFIX}.reasonRequired`));
    setError(null);
    setIsPending(true);
    try {
      await putAuth(
        `${COORDINATOR_ADD_REQUESTS_URL}/${row.id}/status/rejected`,
        { Authorization: `Bearer ${currentUser?.token}` },
        {},
        { reason: reason.trim() }
      );
      onDone();
    } catch (err) {
      setError(serverText(err, t(`${PREFIX}.errorSaving`)));
    } finally {
      setIsPending(false);
    }
  };

  return (
    <MainDialogLayout
      open
      onOpenChange={(open: boolean) => !open && !isPending && onClose()}
      title={t(`${PREFIX}.rejectTitle`, { name: nameOf(row) })}
      maxWidth="520px"
      fitContent
    >
      <div className={peopleStyles.form}>
        <div className={styles.reasonSection}>
          <label htmlFor="coordinator-add-reject-reason" className={styles.reasonLabel}>
            {t(`${PREFIX}.reason`)}
          </label>
          <textarea
            id="coordinator-add-reject-reason"
            rows={3}
            maxLength={500}
            className={styles.reasonInput}
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              if (error) setError(null);
            }}
          />
          <p className={styles.reasonHint}>{t(`${PREFIX}.reasonHint`, { name: row.requestedByName || row.requestedByEmail || '' })}</p>
          {error && (
            <p className={styles.formError} role="alert">
              {error}
            </p>
          )}
        </div>
        <div className={peopleStyles.dialogActions}>
          <Button variant="primary" onClick={submit} disabled={isPending}>
            {isPending ? t('loading') : t(`${PREFIX}.rejectConfirm`)}
          </Button>
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            {t(`${PREFIX}.cancel`)}
          </Button>
        </div>
      </div>
    </MainDialogLayout>
  );
};

/** «אישור»: the office's own add, with every check it makes; a refusal is shown here and the request waits. */
const ApproveDialog = ({ row, onClose, onDone }: { row: CoordinatorAddRequestDtoOut; onClose: () => void; onDone: () => void }) => {
  const { t } = useTranslation();
  const { currentUser } = useAuthContext();
  const [error, setError] = useState<string | null>(null);
  const [isPending, setIsPending] = useState(false);

  const approve = async () => {
    setError(null);
    setIsPending(true);
    try {
      await putAuth(`${COORDINATOR_ADD_REQUESTS_URL}/${row.id}/status/approved`, { Authorization: `Bearer ${currentUser?.token}` });
      onDone();
    } catch (err) {
      setError(serverText(err, t(`${PREFIX}.errorSaving`)));
    } finally {
      setIsPending(false);
    }
  };

  return (
    <ConfirmationDialog
      open
      onOpenChange={(open) => {
        if (!open && !isPending) onClose();
      }}
      title={t(`${PREFIX}.approveTitle`, { name: nameOf(row) })}
      message={t(`${PREFIX}.approveMessage`, { name: nameOf(row), fund: fundOf(row), email: row.email })}
      confirmText={isPending ? 'saving' : `${PREFIX}.approveConfirm`}
      cancelText={`${PREFIX}.cancel`}
      onConfirm={approve}
      onCancel={onClose}
      isLoading={isPending}
      error={error}
    />
  );
};

/**
 * «בקשות הוספת רכז» (SPR-10486): a lead coordinator no longer adds a coordinator on their own - they
 * send the details a fund opening asks of a coordinator, and the office decides here. Approving adds
 * the coordinator and mails them the invite; rejecting mails the lead coordinator the reason.
 */
const CoordinatorAddRequestsPage = () => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [approveRow, setApproveRow] = useState<CoordinatorAddRequestDtoOut | null>(null);
  const [rejectRow, setRejectRow] = useState<CoordinatorAddRequestDtoOut | null>(null);
  const [openRow, setOpenRow] = useState<CoordinatorAddRequestDtoOut | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const { data: rows, isLoading, isError, refetch } = useGetAuthApi<CoordinatorAddRequestDtoOut[]>(COORDINATOR_ADD_REQUESTS_URL);

  const done = (text: string) => {
    setApproveRow(null);
    setRejectRow(null);
    setOpenRow(null);
    setNotice(text);
    void refetch();
  };

  const columns: ColumnDef<CoordinatorAddRequestDtoOut>[] = [
    {
      id: 'fund',
      header: Col(t(`${PREFIX}.fund`)),
      accessorFn: (row) => fundOf(row),
      cell: ({ row }) => (
        <span className={`body-md-regular ${LINE}`} title={fundOf(row.original)}>
          {fundOf(row.original)}
        </span>
      ),
      meta: { className: 'min-w-[180px]' },
    },
    {
      id: 'coordinator',
      header: Col(t(`${PREFIX}.coordinator`)),
      accessorFn: (row) => `${nameOf(row)} ${row.email}`,
      enableSorting: false,
      cell: ({ row }) => {
        const idAndCity = t(`${PREFIX}.idAndCity`, { id: row.original.israeliId, city: row.original.city });
        return (
          <span className="flex min-w-0 flex-col">
            <span className={NAME_LINE}>
              <span className={NAME} title={nameOf(row.original)}>
                {nameOf(row.original)}
              </span>
              <bdi dir="ltr" className={BESIDE} title={row.original.email}>
                {row.original.email}
              </bdi>
            </span>
            <span className={SUB} title={`${row.original.phone} · ${idAndCity}`}>
              <bdi dir="ltr">{row.original.phone}</bdi>
              {` · ${idAndCity}`}
            </span>
          </span>
        );
      },
      meta: { className: 'min-w-[220px]' },
    },
    {
      id: 'permission',
      header: Col(t(`${PREFIX}.permission`)),
      enableSorting: false,
      // A mix is spelled out world by world under the chip - one line, cut, in full on hover and in the blade.
      cell: ({ row }) => (
        <span className={`${LINE} min-w-0`} title={describeWorlds(row.original.worlds, t)}>
          <PermissionChip person={{ worlds: row.original.worlds, permissionLevel: summaryLevel(row.original.worlds) }} />
        </span>
      ),
      meta: { className: 'min-w-[200px]' },
    },
    {
      id: 'requestedBy',
      header: Col(t(`${PREFIX}.requestedBy`)),
      accessorFn: (row) => row.requestedAt,
      cell: ({ row }) => (
        <span className="flex min-w-0 flex-col">
          <span className={`body-md-regular ${LINE}`} title={row.original.requestedByName || row.original.requestedByEmail || ''}>
            {row.original.requestedByName || row.original.requestedByEmail}
          </span>
          <span className={SUB}>{formatDateLocale(asUtc(row.original.requestedAt))}</span>
        </span>
      ),
      meta: { className: 'min-w-[160px]' },
    },
    {
      id: 'actions',
      header: Col(t(`${PREFIX}.actions`)),
      enableSorting: false,
      cell: ({ row }) => (
        <span className="flex gap-2 [&>button]:shrink-0">
          <Button
            variant="primary"
            onClick={(e) => {
              e.stopPropagation();
              setNotice(null);
              setApproveRow(row.original);
            }}
          >
            {t(`${PREFIX}.approve`)}
          </Button>
          <Button
            variant="outline"
            onClick={(e) => {
              e.stopPropagation();
              setNotice(null);
              setRejectRow(row.original);
            }}
          >
            {t(`${PREFIX}.reject`)}
          </Button>
        </span>
      ),
      meta: { className: 'min-w-[200px]', lockLayout: true },
    },
  ];

  return (
    <div>
      {isError && <Alert type="error" message={t(`${PREFIX}.errorLoading`)} />}
      {notice && <Alert type="success" message={notice} onClose={() => setNotice(null)} />}
      <DataTable
        title={t(`${PREFIX}.title`)}
        titleCaption={<span className="body-md-regular text-(--font-body-2)">{t(`${PREFIX}.intro`)}</span>}
        data={rows ?? []}
        isLoading={isLoading}
        columns={columns}
        getRowId={(row) => `${row.id}`}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        customGlobalSearch={searchRow}
        twoLineRows
        handleRowClick={(row) => {
          setNotice(null);
          setOpenRow(row);
        }}
        emptyStateContent={{ title: t(`${PREFIX}.emptyTitle`), description: t(`${PREFIX}.emptyText`) }}
      />
      {openRow && (
        <CoordinatorAddRequestBlade
          key={openRow.id}
          request={openRow}
          onClose={() => setOpenRow(null)}
          onApprove={(request) => setApproveRow(request)}
          onReject={(request) => setRejectRow(request)}
        />
      )}
      {approveRow && (
        <ApproveDialog
          key={approveRow.id}
          row={approveRow}
          onClose={() => setApproveRow(null)}
          onDone={() => done(t(`${PREFIX}.approved`, { name: nameOf(approveRow), fund: fundOf(approveRow) }))}
        />
      )}
      {rejectRow && (
        <RejectDialog
          key={rejectRow.id}
          row={rejectRow}
          onClose={() => setRejectRow(null)}
          onDone={() => done(t(`${PREFIX}.rejected`, { name: nameOf(rejectRow) }))}
        />
      )}
    </div>
  );
};

export default CoordinatorAddRequestsPage;
